/**
 * "Week by week" (CU-869fmjzrg D-C, CA 14): the table that was Pricing Environment. The server hands back each
 * provider's Min / Avg / Max and how many prices, per office, pick-up date and capture week; this composes the table:
 * the statistic, You pinned on top and Market median under it (UX D5), the change against the previous **selected**
 * week in calendar order (T3), and the cheapest (L) / dearest (H) among the providers shown (T6). Tier / Providers and
 * Min / Avg / Max never ask the server again.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { addDays, fromIso, toIso } from '../../../@services/dates.js'
import { median } from '../../../@services/stats.js'

export const THIN_PRICES = 5

/** The selected weeks in calendar order; each says whether the one before it in the selection is the week just before */
export function weekHeaders(weeks = []) {
	const sorted = [...weeks].sort((a, b) => String(a.key).localeCompare(String(b.key)))
	return sorted.map((w, i) => {
		const prev = sorted[i - 1] || null
		const adjacent = !!prev && toIso(addDays(fromIso(prev.to), 1)) === w.from
		return { ...w, prev: prev ? prev.key : null, first: !prev, versus: prev && !adjacent ? `vs ${prev.label}` : null }
	})
}

const value = (raw, stat) => (raw && raw[stat] !== null && raw[stat] !== undefined ? raw[stat] : null)

function rowCells(raw, pickups, weeks, stat) {
	const out = {}
	for (const pickup of pickups) {
		out[pickup] = {}
		for (const w of weeks) {
			const r = raw?.[pickup]?.[w.key]
			const v = value(r, stat)
			const p = w.prev ? value(raw?.[pickup]?.[w.prev], stat) : null
			out[pickup][w.key] = {
				value: v, n: r?.n || 0, thin: v !== null && (r?.n || 0) < THIN_PRICES,
				prev: p, eur: v !== null && p !== null ? v - p : null, pct: v !== null && p ? v / p - 1 : null,
				noPrev: v !== null && !w.first && p === null, low: false, high: false,
			}
		}
	}
	return out
}

/**
 * `{ weeks, pickupDates, offices: [{ id, name, iata, label, empty, rows: [{ key, kind, name, providerId, tag, competitors, cells }] }] }`.
 * `visible` = the providers shown (a Set of ids, `null` = all); your brand always stays.
 */
export function buildWeekly(data, { stat = 'min', visible = null } = {}) {
	const identity = data?.identity || null
	const weeks = weekHeaders(data?.weeks || [])
	const pickups = data?.pickupDates || []
	const shown = visible ? new Set([...visible].map(Number)) : null
	const providers = (data?.providers || []).map(p => ({ ...p, own: identity ? p.own || null : null }))
		.filter(p => !shown || p.own === 'primary' || shown.has(Number(p.id)))
	const you = providers.find(p => p.own === 'primary') || null
	const others = providers.filter(p => p !== you).sort((a, b) => String(a.name).localeCompare(String(b.name)))
	const competitors = others.filter(p => !p.own)

	const offices = (data?.offices || []).map(o => {
		const raw = data?.cells?.[String(o.id)] || {}
		const rows = []
		// cells come office → pick-up → provider → week; each row reads its provider's
		const byProvider = p => Object.fromEntries(pickups.map(d => [d, raw[d]?.[String(p.id)] || {}]))
		const make = (p, kind) => ({
			key: `p${p.id}`, kind, name: p.name, providerId: Number(p.id), tag: kind === 'you' ? 'You' : p.own === 'other' ? 'Yours' : null,
			cells: rowCells(byProvider(p), pickups, weeks, stat),
		})
		if (you) rows.push(make(you, 'you'))

		// Market median: the median of the competitors shown that have a price in that cell; its change week on week
		const med = {}
		for (const d of pickups) {
			med[d] = {}
			for (const w of weeks) {
				const at = key => median(competitors.map(p => value(raw[d]?.[String(p.id)]?.[key], stat)))
				const v = at(w.key)
				const p = w.prev ? at(w.prev) : null
				const n = competitors.reduce((s, c) => s + (raw[d]?.[String(c.id)]?.[w.key]?.n || 0), 0)
				med[d][w.key] = { value: v, n, thin: v !== null && n < THIN_PRICES, prev: p, eur: v !== null && p !== null ? v - p : null, pct: v !== null && p ? v / p - 1 : null, noPrev: v !== null && !w.first && p === null, low: false, high: false }
			}
		}
		rows.push({ key: 'median', kind: 'median', name: 'Market median', competitors: competitors.length, cells: med })
		for (const p of others) rows.push(make(p, 'other'))

		// L / H among the providers shown (median aside), only where at least two have a price
		for (const d of pickups) {
			for (const w of weeks) {
				const priced = rows.filter(r => r.kind !== 'median' && r.cells[d][w.key].value !== null)
				if (priced.length < 2) continue
				const vals = priced.map(r => r.cells[d][w.key].value)
				const lo = Math.min(...vals), hi = Math.max(...vals)
				if (lo === hi) continue
				for (const r of priced) {
					r.cells[d][w.key].low = r.cells[d][w.key].value === lo
					r.cells[d][w.key].high = r.cells[d][w.key].value === hi
				}
			}
		}
		const empty = !pickups.some(d => Object.keys(raw[d] || {}).length)
		return { id: o.id, name: o.name, iata: o.iata || null, label: o.iata ? `${o.name} ${o.iata}` : o.name, empty, rows }
	})

	return { weeks, pickupDates: pickups, offices, identity, daily: !!data?.daily, competitors: competitors.length, total: providers.filter(p => p.own !== 'primary').length }
}
